import { MyImage } from '~/components/ui/MyImage'
import { MyModal } from '~/components/ui/MyModal'
import {
  FenToYuan,
  graphQLErrorMessage,
  isAmount,
  toYMDHms,
  YuanToFen,
} from '~/utils'
import { InfoCard, InfoCardItem } from '~/components/business/pay/InfoCard'
import { InputNumber, message, Switch, Tooltip } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useCallback, useState } from 'react'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react-lite'
import usePay from '~/hooks/usePay'
import { useMutation } from '@apollo/client'
import { toPay, toPayVariables } from '~/graphqls/types'
import { TO_PAY } from '~/graphqls/gqls'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyForm } from '~/components/ui/MyForm'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { MAX_PRICE, MIN_PRICE } from '~/configs'
import { debounce } from 'lodash'
import { OrderSuccessModal } from '~/components/pages/presale/OrderSuccessModal'
import { MyCheckbox } from '~/components/ui/MyCheckbox'

interface Props {
  visible: boolean
  setVisible: (bool: boolean) => void
  product: {
    image?: string
    name?: string
    price: number
    buyType?: number
    seriesId?: any
    endTime?: any
    /** 是否是公售期 */
    hasProductNow: boolean
  }
}

const OrderModal = observer(({ visible, setVisible, product }: Props) => {
  const { store } = useStores()
  const [form] = MyForm.useForm()
  // 是否使用余额
  const [useBalance, setUseBalance] = useState(false)
  const [loading, setLoading] = useState(false)
  // 是否同意协议
  const [isAgree, setIsAgree] = useState(false)

  /** 显示支付成功model */
  const [orderSuccessVisible, setOrderSuccessVisible] = useState(false)
  const [orderNo, setOrderNo] = useState<string>()

  const [payAction] = useMutation<toPay, toPayVariables>(TO_PAY)

  const successCallback = useCallback(() => {
    setOrderSuccessVisible(true)
  }, [setOrderSuccessVisible])

  const payFn = async ({
    pwd,
    preSettlementData,
    currentPayType,
    balancePayFee,
  }: {
    pwd?: string
    preSettlementData: any
    currentPayType: any
    balancePayFee: number
  }) => {
    try {
      setLoading(true)
      const res = await payAction({
        variables: {
          settlement: {
            price: undefined,
            buyType: product.buyType!,
            balancePayFee: balancePayFee,
            channelPayFee: preSettlementData?.totalFee - balancePayFee,
            password: pwd,
            payTypeInfo: {
              payChannel: currentPayType?.payChannel,
              payProvider: currentPayType?.payProvider,
              payType: currentPayType?.type,
            },
            artworkId: product.seriesId,
            useBalance: useBalance,
            usePrice: YuanToFen(usePrice),
            changePrice: YuanToFen(adaptPrice),
          },
        },
      })
      setOrderNo(res.data?.orderModule?.copyrightPay?.orderNo || '')
      return {
        needPay: res.data?.orderModule?.copyrightPay?.needPay,
        orderId: res.data?.orderModule?.copyrightPay?.orderId,
        orderNo: res.data?.orderModule?.copyrightPay?.orderNo,
        payInfo: res.data?.orderModule?.copyrightPay?.payInfo,
      }
    } catch (err) {
      message.error(graphQLErrorMessage(err) || '未知错误')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const {
    currentPayType,
    preSettlementData,
    PayTypeListCard,
    balancePayFee,
    handlePay,
    Slot,
  } = usePay({
    useBalance: useBalance,
    id: product.seriesId,
    buyType: product.buyType,
    payFn: payFn,
    successCallback,
  })

  const handlePayWrapper = async () => {
    await form.validateFields()
    if (!isAgree) {
      message.destroy()
      message.error('请先勾选同意相关协议')
      return
    }
    handlePay()
  }

  const [usePrice, setUsePrice] = useState<number | undefined>(undefined)
  const [adaptPrice, setAdaptPrice] = useState<number | undefined>(undefined)

  return (
    <MyModal
      visible={visible}
      title="订单详情"
      width={448}
      footer={null}
      onCancel={() => setVisible(false)}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
    >
      <div>
        <div className="flex items-center mb-6">
          <MyImage
            tag="v400"
            src={product.image}
            style={{ width: 72, height: 72, borderRadius: 16 }}
            lazy={false}
          />
          <div className="ml-3">
            <div className="text-neutral-100 font-medium text-base mb-1">
              {product.name}
            </div>
            <div>
              <span className="inline-block text-xs px-1 rounded bg-primary text-white mr-1">
                {!product.hasProductNow ? '预售价格' : '公售价格'}
              </span>
              <span className="text-neutral-400 text-xs">版权所有权</span>
              <span className="text-error font-semibold">
                ￥{FenToYuan(preSettlementData?.totalFee || product.price)}
              </span>
            </div>
          </div>
        </div>

        {!product.hasProductNow ? (
          <div className="py-2 px-4 rounded-full bg-neutral-700 my-6">
            <ExclamationCircleOutlined className="mr-2" />
            预计&nbsp;
            <span className="text-error">{toYMDHms(product.endTime)}</span>
            &nbsp;前发放
          </div>
        ) : (
          <MyForm
            layout="vertical"
            initialValues={{
              useprice: usePrice,
              adaptprice: adaptPrice,
            }}
          >
            <MyForm.Item
              label={
                <>
                  标准类授权（使用权）价格
                  <Tooltip
                    placement="topLeft"
                    title="不同使用权价格之间的比例关系，标准类授权：拓展类授权：商品类授权）=1:1.5:3，其他使用权价格将根据标准类授权价格自动计算出出售价格"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 3 }} />
                  </Tooltip>
                </>
              }
              name="useprice"
              rules={[
                {
                  type: 'number',
                  validator(_rule, value) {
                    if (!value) {
                      return Promise.reject('请输入使用权价格')
                    } else if (value < 1) {
                      return Promise.reject(`使用权价格需大于¥${1}`)
                    } else if (value > 1000) {
                      return Promise.reject(`使用权价格需小于¥${1000}`)
                    } else if (!isAmount.test(value)) {
                      return Promise.reject('价格最多支持两位小数')
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <InputNumber
                controls={false}
                addonAfter="CNY"
                value={usePrice}
                onChange={v => {
                  setUsePrice(v || 0)
                }}
                placeholder="请输入使用权价格"
              />
            </MyForm.Item>

            {!!usePrice && (
              <InfoCard style={{ marginBottom: 24 }}>
                <InfoCardItem label="作品使用权价格"></InfoCardItem>
                <InfoCardItem label="标准类授权">
                  ¥ {usePrice.toFixed(2)}
                </InfoCardItem>
                <InfoCardItem label="扩展类授权">
                  ¥ {(usePrice * 1.5).toFixed(2)}
                </InfoCardItem>
                <InfoCardItem label="商品类授权">
                  ¥ {(usePrice * 3).toFixed(2)}
                </InfoCardItem>
              </InfoCard>
            )}

            <MyForm.Item
              label={<>改编权价格</>}
              name="adaptprice"
              rules={[
                {
                  type: 'number',
                  validator(_rule, value) {
                    if (!value) {
                      return Promise.reject('请输入改编权价格')
                    } else if (value < 1) {
                      return Promise.reject(`改编权价格需大于¥1`)
                    } else if (value > 1000) {
                      return Promise.reject(`改编权价格需小于¥1000`)
                    } else if (!isAmount.test(value)) {
                      return Promise.reject('价格最多支持两位小数')
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <InputNumber
                controls={false}
                addonAfter="CNY"
                value={adaptPrice}
                onChange={v => {
                  setAdaptPrice(v || 0)
                }}
                placeholder="请输入改编权价格"
              />
            </MyForm.Item>
          </MyForm>
        )}

        {/* <InfoCard>
          <InfoCardItem label={<>可用余额</>}>
            ¥{FenToYuan(preSettlementData?.balance)}
          </InfoCardItem>
          <InfoCardItem label={<>余额支付</>}>
            <Switch
              checked={useBalance}
              onChange={setUseBalance}
              disabled={preSettlementData?.balance <= 0}
            />
          </InfoCardItem>
        </InfoCard> */}

        <PayTypeListCard />

        <div>
          <MyCheckbox
            checked={isAgree}
            onChange={e => setIsAgree(e.target.checked)}
          >
            <div className="text-neutral-400">
              我已阅读并同意
              <a
                href={'/us/ownership'}
                target="_blank"
                rel="noreferrer"
                className="text-neutral-200"
              >
                《购买协议》
              </a>
            </div>
          </MyCheckbox>
        </div>

        <div className="flex items-center justify-between mt-6 py-3">
          <div>
            {useBalance && (
              <div className="text-neutral-400 text-xs">
                余额支付{' '}
                <span className="text-error text-base">
                  ¥{FenToYuan(balancePayFee)}
                </span>
              </div>
            )}
            {preSettlementData?.totalFee - balancePayFee > 0 && (
              <div className="text-neutral-400 text-xs">
                {currentPayType?.name}{' '}
                <span className="text-error text-base">
                  ¥{FenToYuan(preSettlementData?.totalFee - balancePayFee)}
                </span>
              </div>
            )}
          </div>

          <MyBtn
            type="primary"
            onClick={handlePayWrapper}
            loading={loading}
            className="!px-8"
          >
            确认支付
          </MyBtn>
        </div>
      </div>

      <Slot />
      {orderSuccessVisible && (
        <OrderSuccessModal
          visible={orderSuccessVisible}
          setVisible={setOrderSuccessVisible}
          orderNo={orderNo!}
        />
      )}
    </MyModal>
  )
})

export default OrderModal
